// webapp/frontend/src/components/ThreatNetwork.js
// Add to Dashboard.js:
//   import ThreatNetwork from '../components/ThreatNetwork';
//   <ThreatNetwork /> (anywhere in the dashboard layout)

import { useState, useEffect } from "react";
import supabase from "../lib/supabase";

const THREAT_COLORS = {
  high:    { bg: "rgba(226,75,74,0.08)",  color: "#f09595", border: "rgba(226,75,74,0.2)" },
  medium:  { bg: "rgba(239,159,39,0.08)", color: "#FAC775", border: "rgba(239,159,39,0.2)" },
  low:     { bg: "rgba(13,148,136,0.08)", color: "#0D9488", border: "rgba(13,148,136,0.2)" },
};

const PLATFORM_ICONS = {
  leak_site: "◈",
  forum:     "◎",
  tube:      "◉",
  dark_web:  "◆",
  messaging: "◇",
  unknown:   "○",
};

function threatLevel(score) {
  if (score >= 0.75) return "high";
  if (score >= 0.55) return "medium";
  return "low";
}

function ScoreBar({ score }) {
  const level = threatLevel(score);
  const { color } = THREAT_COLORS[level];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${Math.round(score * 100)}%`, height: "100%", background: color, borderRadius: 2, transition: "width 0.5s ease" }} />
      </div>
      <span style={{ fontSize: 11, color, minWidth: 30, textAlign: "right" }}>{Math.round(score * 100)}%</span>
    </div>
  );
}

export default function ThreatNetwork() {
  const [sources, setSources]   = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter]     = useState("all");

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("threat_sources")
        .select("*")
        .eq("is_active", true)
        .order("threat_score", { ascending: false });

      const sources = data || [];
      setSources(sources);
      setStats({
        total:       sources.length,
        detections:  sources.reduce((a, s) => a + (s.detection_count || 0), 0),
        high_threat: sources.filter(s => s.threat_score >= 0.75).length,
        new_this_week: sources.filter(s => {
          const d = new Date(s.first_seen_at);
          return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
        }).length,
      });
    } catch (e) {
      console.error("ThreatNetwork load error:", e);
    }
    setLoading(false);
  }

  const filtered = filter === "all"
    ? sources
    : sources.filter(s => s.platform_type === filter);

  const S = {
    panel: {
      background: "rgba(255,255,255,0.02)",
      border: "0.5px solid rgba(255,255,255,0.08)",
      borderRadius: 14,
      padding: "24px",
      marginTop: 20,
    },
    statBox: {
      background: "rgba(255,255,255,0.03)",
      border: "0.5px solid rgba(255,255,255,0.06)",
      borderRadius: 10,
      padding: "14px 16px",
      flex: 1,
      minWidth: 90,
    },
    filterBtn: (active) => ({
      fontSize: 12,
      padding: "5px 12px",
      borderRadius: 20,
      border: `0.5px solid ${active ? "rgba(13,148,136,0.4)" : "rgba(255,255,255,0.1)"}`,
      background: active ? "rgba(13,148,136,0.1)" : "transparent",
      color: active ? "#0D9488" : "rgba(240,237,232,0.4)",
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all 0.15s",
    }),
    row: {
      padding: "10px 0",
      borderBottom: "0.5px solid rgba(255,255,255,0.05)",
      cursor: "pointer",
    },
  };

  return (
    <div style={S.panel}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#0D9488", letterSpacing: "0.08em", marginBottom: 4 }}>THREAT NETWORK</div>
          <div style={{ fontSize: 13, color: "rgba(240,237,232,0.5)" }}>
            Shared intelligence from creators across the network
          </div>
        </div>
        <button onClick={loadData} style={{ background: "rgba(13,148,136,0.1)", border: "0.5px solid rgba(13,148,136,0.2)", borderRadius: 6, padding: "6px 14px", fontSize: 12, color: "#0D9488", cursor: "pointer", fontFamily: "inherit" }}>
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={S.statBox}>
            <div style={{ fontSize: 22, fontWeight: 500, color: "#f0ede8" }}>{stats.total}</div>
            <div style={{ fontSize: 11, color: "rgba(240,237,232,0.4)", marginTop: 2 }}>platforms monitored</div>
          </div>
          <div style={S.statBox}>
            <div style={{ fontSize: 22, fontWeight: 500, color: "#f0ede8" }}>{stats.detections}</div>
            <div style={{ fontSize: 11, color: "rgba(240,237,232,0.4)", marginTop: 2 }}>total detections</div>
          </div>
          <div style={S.statBox}>
            <div style={{ fontSize: 22, fontWeight: 500, color: "#f09595" }}>{stats.high_threat}</div>
            <div style={{ fontSize: 11, color: "rgba(240,237,232,0.4)", marginTop: 2 }}>high-risk sources</div>
          </div>
          <div style={S.statBox}>
            <div style={{ fontSize: 22, fontWeight: 500, color: "#0D9488" }}>{stats.new_this_week}</div>
            <div style={{ fontSize: 11, color: "rgba(240,237,232,0.4)", marginTop: 2 }}>new this week</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "leak_site", "forum", "tube", "dark_web"].map(f => (
          <button key={f} style={S.filterBtn(filter === f)} onClick={() => setFilter(f)}>
            {f === "all" ? "All" : f.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Source list */}
      {loading && (
        <div style={{ fontSize: 13, color: "rgba(240,237,232,0.3)", padding: "12px 0" }}>Loading network...</div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ fontSize: 13, color: "rgba(240,237,232,0.3)", padding: "8px 0" }}>No sources found.</div>
      )}

      {filtered.map((s, i) => {
        const level = threatLevel(s.threat_score);
        const { bg, color, border } = THREAT_COLORS[level];
        const icon = PLATFORM_ICONS[s.platform_type] || "○";
        const isOpen = expanded === s.id;

        return (
          <div key={s.id} style={S.row} onClick={() => setExpanded(isOpen ? null : s.id)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 14, color: "#0D9488", flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(240,237,232,0.85)" }}>{s.domain}</span>
                  <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: bg, color, border: `0.5px solid ${border}`, flexShrink: 0 }}>
                    {level} risk
                  </span>
                </div>
                <ScoreBar score={s.threat_score} />
              </div>
              <div style={{ fontSize: 11, color: "rgba(240,237,232,0.3)", flexShrink: 0, textAlign: "right" }}>
                <div>{s.detection_count} detection{s.detection_count !== 1 ? "s" : ""}</div>
              </div>
            </div>

            {isOpen && (
              <div style={{ paddingLeft: 24, paddingTop: 6, paddingBottom: 4 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 8 }}>
                  {[
                    ["Type", s.platform_type?.replace("_", " ") || "unknown"],
                    ["First seen", s.first_seen_at ? new Date(s.first_seen_at).toLocaleDateString() : "—"],
                    ["Last seen", s.last_seen_at ? new Date(s.last_seen_at).toLocaleDateString() : "—"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: "rgba(240,237,232,0.3)", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 12, color: "rgba(240,237,232,0.7)" }}>{value}</div>
                    </div>
                  ))}
                </div>
                {s.notes && (
                  <div style={{ fontSize: 12, color: "rgba(240,237,232,0.4)", fontStyle: "italic" }}>{s.notes}</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 16, fontSize: 12, color: "rgba(240,237,232,0.2)", borderTop: "0.5px solid rgba(255,255,255,0.05)", paddingTop: 12 }}>
        Threat intelligence is pooled anonymously across all Creator Protect members. Every leak detected makes the network smarter for everyone.
      </div>
    </div>
  );
}
